import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Calendar, MapPin } from 'lucide-react';
import { getLatestEvents, getLatestAnnouncements } from '../services/api';
import NewsCard from './NewsCard';

const NewsSection = () => {
    const [events, setEvents] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [mobileActiveIndex, setMobileActiveIndex] = useState(0);

    const handleMobileScroll = (e) => {
        const scrollPosition = e.target.scrollLeft;
        const cardWidth = e.target.firstElementChild?.offsetWidth || 300;
        const index = Math.round(scrollPosition / cardWidth);
        setMobileActiveIndex(index);
    };
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [latestEvents, latestAnnouncements] = await Promise.all([
                    getLatestEvents(5),
                    getLatestAnnouncements(3)
                ]);

                setEvents(latestEvents);
                setAnnouncements(latestAnnouncements);
            } catch (error) {
                console.error("Veri çekme hatası:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();

        const checkMobile = () => setIsMobile(window.innerWidth < 1024);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Auto-scroll for slider (only if events exist AND not mobile)
    useEffect(() => {
        if (events.length === 0 || isMobile) return;
        const interval = setInterval(() => {
            setActiveIndex((current) => (current + 1) % events.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [events.length, isMobile]);

    const nextSlide = () => {
        if (events.length > 0) setActiveIndex((current) => (current + 1) % events.length);
    };

    const prevSlide = () => {
        if (events.length > 0) setActiveIndex((current) => (current - 1 + events.length) % events.length);
    };

    if (loading) {
        return <div className="py-20 text-center text-gray-500">Yükleniyor...</div>;
    }

    if (events.length === 0) return null;

    return (
        <section className="relative z-30 -mt-8 sm:-mt-16 md:-mt-24 pb-20">
            <div className="w-full max-w-[95%] mx-auto px-4 md:px-8">
                {/* Modern Glass Container */}
                <div className="bg-[#0f172a]/90 backdrop-blur-xl rounded-[2.5rem] p-4 md:p-8 shadow-2xl border border-white/10 relative overflow-hidden">

                    <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch pt-2">

                        {/* LEFT: Announcements Section */}
                        <div className="lg:col-span-5 flex flex-col h-full min-h-0 lg:min-h-[400px]">
                            <div className="flex items-center justify-between mb-4 md:mb-6 px-2">
                                <h2 className="text-3xl md:text-5xl font-black font-heading tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-yellow-100 to-yellow-500">
                                    Duyurular
                                </h2>
                            </div>

                            <div className="bg-white/5 rounded-3xl border border-white/10 p-0 md:p-6 h-full flex flex-col hover:border-white/20 transition-colors shadow-lg overflow-hidden md:overflow-visible">
                                <div className="space-y-3 md:space-y-4 flex-1 overflow-y-visible md:overflow-y-auto pr-0 md:pr-2 custom-scrollbar p-4 md:p-0">
                                    {announcements.length === 0 ? (
                                        <div className="flex items-center justify-center h-full text-gray-500 text-sm italic py-10">
                                            Şu an aktif duyuru bulunmamaktadır.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="space-y-3 md:space-y-4">
                                                {announcements.map((announcement) => (
                                                    <Link
                                                        to={`/duyurular/${announcement.id}`}
                                                        key={announcement.id}
                                                        className="block p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all border border-white/5 hover:border-yellow-500/30 group relative"
                                                    >
                                                        <div className="flex flex-col gap-2">
                                                            {/* Clean Date Line */}
                                                            <div className="flex items-center gap-2 text-yellow-500 text-xs font-mono tracking-wider">
                                                                <Calendar className="w-3 h-3" />
                                                                <span>{announcement.date}</span>
                                                            </div>
                                                            {/* Title */}
                                                            <h4 className="font-bold text-base text-gray-200 group-hover:text-yellow-400 transition-colors leading-snug">
                                                                {announcement.title}
                                                            </h4>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                            <div className="pt-4 mt-2 border-t border-white/5">
                                                <Link to="/duyurular" className="group flex items-center justify-center gap-2 py-3.5 md:py-3 rounded-xl bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500 hover:text-black transition-all font-bold text-sm">
                                                    Tüm Duyuruları Gör
                                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                </Link>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Events Section */}
                        <div className="lg:col-span-7 flex flex-col h-full">
                            <div className="flex items-center justify-between mb-6 px-2">
                                <h2 className="text-3xl md:text-5xl font-black font-heading tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                                    Etkinlikler
                                </h2>
                                <Link to="/etkinlikler" className="group flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                                    Tümünü Gör
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>

                            {/* Mobile: Single Horizontal Slider (Featured + Others) */}
                            {isMobile ? (
                                <div className="flex flex-col gap-4">
                                    <div
                                        className="flex overflow-x-auto pb-4 gap-0 snap-x snap-mandatory hide-scrollbar items-stretch"
                                        onScroll={handleMobileScroll}
                                    >
                                        {events.map((event, idx) => (
                                            <div
                                                key={event.id}
                                                className="snap-center shrink-0 w-full"
                                            >
                                                <NewsCard
                                                    event={event}
                                                    layout="card"
                                                    compact={false}
                                                    index={idx}
                                                    className="h-full shadow-md"
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Mobile Dots Indicator */}
                                    <div className="flex justify-center gap-2 pb-4">
                                        {events.map((_, idx) => (
                                            <div
                                                key={idx}
                                                className={`h-1.5 rounded-full transition-all duration-300 ${idx === mobileActiveIndex
                                                    ? 'w-6 bg-cyan-400'
                                                    : 'w-1.5 bg-gray-600 hover:bg-gray-500'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                /* Desktop: Slider */
                                <div className="relative group flex-1 rounded-3xl overflow-hidden shadow-2xl border border-white/5 bg-black/40 min-h-[400px]">
                                    <AnimatePresence mode='wait'>
                                        {events[activeIndex] && (
                                            <NewsCard
                                                key={events[activeIndex].id}
                                                event={events[activeIndex]}
                                                compact={false}
                                                featured={true}
                                                index={activeIndex}
                                            />
                                        )}
                                    </AnimatePresence>

                                    {/* Controls Overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 p-6 flex justify-between items-end bg-gradient-to-t from-black/80 via-black/40 to-transparent z-50 pointer-events-none">
                                        <div className="flex gap-2 pointer-events-auto mb-2">
                                            {events.map((_, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => setActiveIndex(idx)}
                                                    className={`h-1.5 rounded-full transition-all duration-300 ${idx === activeIndex ? 'w-8 bg-cyan-400' : 'w-2 bg-white/30 hover:bg-white/50'}`}
                                                />
                                            ))}
                                        </div>
                                        <div className="flex gap-2 pointer-events-auto">
                                            <button onClick={prevSlide} className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 text-white transition-colors border border-white/20">
                                                <ChevronLeft className="w-5 h-5" />
                                            </button>
                                            <button onClick={nextSlide} className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 text-white transition-colors border border-white/20">
                                                <ChevronRight className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default NewsSection;
